import { DigitalWalletManagementRowResult } from "@/models/digital.wallet";
import digitalWalletStore from "@/stores/digital.wallet.store";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";
import { formatDate, formatAmount } from "@/util";
import { Box, Grid } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import StatusActive from "../../assets/icons/status-active.svg";
import StatusInactive from "../../assets/icons/status-inactive.svg";
import LinkElementStyled from "../styled/link.element.styled";
import { useNavigate } from "react-router-dom";
import filtersStore from "@/stores/filtersStore";
import DetailButtonStyled from "../styled/detail.button.styled";

interface IDigitalWalletManagementGrid {
    sortBy: Function;
    wallets: DigitalWalletManagementRowResult[];
    isSearch: boolean;
    fetchMore: Function;
    hasNext: boolean;
    waletSelectionModel: GridRowSelectionModel;
    setWalletSelectionModel: Function;
}
const DigitalWalletManagementGrid: FC<IDigitalWalletManagementGrid> = (props: IDigitalWalletManagementGrid) => {
    const { t } = useTranslation();
    const employeeNumber = t("general.SearchFilterEmployeesNumber");
    const employeeName = t("transaction.TransactionEmployeeName");
    const employeeStatus = t("transaction.TransactionEmployeeStatus");
    const employeeGroup = t("transaction.TransactionEmployeeGroup");
    const walletExpiryDate = t("digitalWallet.ExpirationDate");
    const lastLoadingDate = t("digitalWallet.LastLoadingDate");
    const walletStatus = t("general.SearchFilterWalletStatus");
    const currentBalance = t("digitalWallet.CurrentBalance");
    const organizationUnit = t("employee.Department");

    const navigate = useNavigate();

    function convertSelectionToArray(selection: GridRowSelectionModel): number[] {
        if (selection) {
            const selectedIds = selection.map((id) => parseInt(id.toString(), 10));
            return selectedIds;
        }
        return [];
    }

    const onChangeSelectionHandle = (row: GridRowSelectionModel) => {
        digitalWalletStore.setWalletSelectionModel(convertSelectionToArray(row));
        props.setWalletSelectionModel(row);
    };

    const openEmployeePage = (id: number | undefined, employeeName: string) => {
        if (!id) {
            return;
        }

        filtersStore.setEmployeeWalletFilterData(null);
        filtersStore.setEmployeeOrderFilterData(null);
        filtersStore.setEmployeeDocFilterData(null);
        employeeBreadcrumbStore.update(employeeName);
        navigate({
            pathname: `/employees/${id}/info`,
            search: `?returnUrl=${window.location.pathname}`,
        });
    };

    const openTransactions = (id: number | undefined, employeeName: string) => {
        employeeBreadcrumbStore.update(employeeName);
        navigate({
            pathname: "/digital-wallet/list/" + id + "/transactions",
            search: `?returnUrl=${window.location.pathname}`,
        });
    };

    const columns: GridColDef[] = [
        //{ field: "id", headerName: "ID", width: 90 },
        {
            field: "employeeNumber",
            headerName: employeeNumber,
            minWidth: 100,
            headerClassName: "text-align-right",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Grid
                    className="text-align-right"
                    sx={{
                        width: "90%",
                    }}
                >
                    <LinkElementStyled
                        onClick={() => openEmployeePage(params.row.employeeId, params.row.employeeName)}
                        sx={{ cursor: "pointer", fontSize: "16px" }}
                    >
                        {params.row.employeeNumber}
                    </LinkElementStyled>
                </Grid>
            ),
        },
        {
            field: "employeeName",
            headerName: employeeName,
            flex: 1,
        },
        {
            field: "employeeStatus",
            headerName: employeeStatus,
            flex: 1,
            renderCell: (params: GridRenderCellParams<Boolean>) => (
                <Box sx={{ display: "flex" }}>
                    <Box
                        component="img"
                        sx={{
                            height: "8px",
                            width: "8px",
                            marginTop: "6px",
                        }}
                        alt={t("general.Active")}
                        src={params.value ? StatusActive : StatusInactive}
                    />
                    <Box sx={{ padding: "0 5px", fontSize: "16px" }}>
                        {params.value ? t("general.Active") : t("general.Inactive")}
                    </Box>
                </Box>
            ),
        },
        {
            field: "employeeGroup",
            headerName: employeeGroup,
            flex: 1,
        },
        {
            field: "organizationUnit",
            headerName: organizationUnit,
            flex: 1,
        },
        {
            field: "currentBalance",
            headerName: currentBalance,
            headerClassName: "text-align-right",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    sx={{
                        width: "90%",
                        textAlign: "right",
                        fontSize: "16px",
                    }}
                >
                    {formatAmount(params.row.currentBalance, params.row.currencyId)}
                </Box>
            ),
        },
        {
            field: "walletExpiryDate",
            headerName: walletExpiryDate,
            headerClassName: "text-align-right",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    sx={{
                        width: "90%",
                        textAlign: "right",
                        fontSize: "16px",
                    }}
                >
                    {formatDate(params.row.walletExpiryDate)}
                </Box>
            ),
        },
        {
            field: "lastLoadingDate",
            headerName: lastLoadingDate,
            headerClassName: "text-align-right",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Grid
                    className="text-align-right"
                    sx={{
                        width: "90%",
                        fontSize: "16px",
                    }}
                >
                    {params.row.lastLoadingDate && formatDate(params.row.lastLoadingDate)}
                </Grid>
            ),
        },
        {
            field: "walletStatus",
            headerName: walletStatus,
            flex: 1,
            renderCell: (params: GridRenderCellParams<String>) => (
                <Box sx={{ display: "flex" }}>
                    <Box
                        component="img"
                        sx={{
                            height: "8px",
                            width: "8px",
                            marginTop: "6px",
                        }}
                        alt={t("general.Active")}
                        src={params.value === "Active" ? StatusActive : StatusInactive}
                    />
                    <Box sx={{ padding: "0 5px", fontSize: "16px" }}>
                        {t(`enums.DigitalWalletStatus.${params.value}`)}
                    </Box>
                </Box>
            ),
        },
        {
            field: "walletTransactions",
            headerName: "",
            sortable: false,
            minWidth: 140,
            renderCell: (params: GridRenderCellParams) => (
                <Grid>
                    <DetailButtonStyled onClick={() => openTransactions(params.row.id, params.row.employeeName)}>
                        {t("breadcrumbs.DigitalWalletTransactions")}
                    </DetailButtonStyled>
                </Grid>
            ),
        },
    ];

    return (
        <Grid className="wallet-management-data-grid-block">
            <Box sx={{ width: "100%", height: "calc(100vh - 525px)" }}>
                <DataGrid
                    rows={props.wallets}
                    columns={columns}
                    // initialState={{
                    //     pagination: {
                    //         paginationModel: {
                    //             pageSize: 5,
                    //         },
                    //     },
                    // }}
                    //pageSizeOptions={[5]}
                    checkboxSelection
                    //disableRowSelectionOnClick
                    hideFooter
                    rowCount={props.wallets.length}
                    paginationMode="server"
                    disableColumnFilter={true}
                    disableColumnMenu
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        onChangeSelectionHandle(newRowSelectionModel);
                    }}
                    rowSelectionModel={props.waletSelectionModel}
                />
            </Box>
        </Grid>
    );
};

export default DigitalWalletManagementGrid;
